/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-runloop */
import { hash } from '@ember/helper';
import { action, computed, get, set } from '@ember/object';
import { getOwner } from '@ember/owner';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { scheduleOnce } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismLoadingBar,
  PrismModal,
  PrismTabsContainer,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { OTB_DEFAULT_VALUE } from 'frontend-utils/utils/constants/constant-values';
import GeneralButtonsButtonExport from 'lumen/components/general/buttons/button-export';
import GeneralButtonsOtbSelfServiceButton from 'lumen/components/general/buttons/otb-self-service-button';
import GeneralDayDetailDayDetailEvents from 'lumen/components/general/day-detail/day-detail-events';
import GeneralDayDetailTabsTabBookingCurve from 'lumen/components/general/day-detail/tabs/tab-booking-curve';
import GeneralDayDetailTabsTabRatesEvolution from 'lumen/components/general/day-detail/tabs/tab-rates-evolution';
import GeneralModalsModalHeaderDateSelection from 'lumen/components/general/modals/modal-header-date-selection';
import HotelPricingAssistantDayDetailTabsTabOpenPricing from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/tab-open-pricing';
import HotelPricingAssistantDayDetailTabsTabPricing from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/tab-pricing';
import type { Day } from 'lumen/models';
import { RESIZE_EVENTS } from 'lumen/services/resize';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import {
  MAX_ALLOWED_MONTHS_IN_FUTURE,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { DETAIL_TABS } from 'lumen/utils/constants/rate-constants';
import { OutOfOrderRooms } from 'lumen/utils/models/occupancy';

const HEADER_HEIGHT = 60;

interface GeneralDayDetailDayDetailSignature {
  Args: {
    activeCompset: unknown;
    changeDay: unknown;
    currency: unknown;
    currentHotel: unknown;
    date: unknown;
    edit: unknown;
    hasOtbSelfServiceEntryPoint: unknown;
    isBestFlex: unknown;
    isPricingAssistantProduct: unknown;
    model: unknown;
    offset: unknown;
    onToggleEdit: unknown;
    otaId: unknown;
    paceTabFilterMessage: unknown;
    ratesEvolutionDisabled: unknown;
    ratesParams: unknown;
    redirect: unknown;
    selectedOTA: unknown;
    type: unknown;
    updateOffset: unknown;
    updateType: unknown;
  };
}

export default class GeneralDayDetailDayDetail extends Component<GeneralDayDetailDayDetailSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare myotbSs: Services['myotb-ss'];
  @service declare resize: Services['resize'];
  @service declare simpleExport: Services['simple-export'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  height = 600;
  width = 600;

  /**
   * Checks whether the current date is the earliest possible date
   * @return {Boolean} True if you have reached ratesDataStart
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get beginReached() {
    const previousDate = this.hotelAndMarketService
      // @ts-expect-error: Incorrect type
      .toLocalTime(this.args.date)
      .subtract(1, 'day');

    // In pricing assistant, we can go back only until today
    if (this.args.isPricingAssistantProduct) {
      const pricingModeDataStart = this.today;
      return previousDate.isBefore(pricingModeDataStart, 'day');
    }

    return previousDate.isBefore(
      this.hotelAndMarketService.ratesDataStart,
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get competitorsForCurrentHotelAndOTA() {
    if (this.args.activeCompset) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return this.args.activeCompset?.competitors.filter(
        // @ts-expect-error: Incorrect type
        (competitor) =>
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          competitor.otas.find((ota) => ota.id === this.args.otaId) !==
          undefined,
      );
    }
    return [];
  }

  // The title for the modal
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get day() {
    return this.dayMoment.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );
  }

  @computed('args.{date,model.loading}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dayModel() {
    // @ts-expect-error: Incorrect type
    if (!this.args.model?.loading) {
      // @ts-expect-error: Incorrect type
      return this.store.peekRecord<Day>('day', this.args.date);
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dayMoment() {
    // @ts-expect-error: Incorrect type
    return this.hotelAndMarketService.toLocalTime(this.args.date);
  }

  /**
   * Checks whether the current date is the latest possible date
   * @return {Boolean} True if you have reached ratesDataEnd
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endReached() {
    const nextDate = this.hotelAndMarketService
      // @ts-expect-error: Incorrect type
      .toLocalTime(this.args.date)
      .add(1, 'day');

    // In pricing assistant, we can go forward only until end day of PA MAX_ALLOWED_MONTHS_IN_FUTURE
    if (this.args.isPricingAssistantProduct) {
      const pricingModeDataEnd = this.today
        .add(MAX_ALLOWED_MONTHS_IN_FUTURE, 'month')
        .endOf('month');
      return nextDate.isAfter(pricingModeDataEnd, 'day');
    }

    return nextDate.isAfter(this.hotelAndMarketService.ratesDataEnd, 'day');
  }

  // Note: Filter out parameters so that we can reuse the display-rate-parity-filters helper for the Pricing case (don't want to show every filter here)
  // In pricing assistant, both pricing and rates evolution tab header should show the following static filters (+ compset filter for latter)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredRatesParamsForPricingAssistant() {
    const allowedParams = ['los', 'ota', 'persons', 'rateParam'];

    // Show compset filter (if available) only for the Rates Evolution tab
    if (this.args.type === DETAIL_TABS.RATE_EVOLUTION) {
      allowedParams.push('compset');
    }

    const filteredParams = {};

    allowedParams.forEach((param) => {
      // @ts-expect-error: Incorrect type
      if (this.args.ratesParams[param]) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        filteredParams[param] = this.args.ratesParams[param];
      }
    });

    return filteredParams;
  }

  /**
   * Used to check whether we can 1. show the Pace tab 2. show the OTB curve in the evolution chart
   * @return {Boolean} True if we can see the Pace tab
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasBookingCurve() {
    // @ts-expect-error: Incorrect type
    return this.access.hasBookingCurveOnDate(this.args.date);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOtbSelfServiceButton() {
    return (
      this.args.hasOtbSelfServiceEntryPoint &&
      !this.myotbSs.isOtbSelfServiceUpsellBannerVisible &&
      this.access.hasFeature('otb.empty_placeholder')
    );
  }

  /**
   * Checks whether we can show the Rates Evolution tab in the day detail
   * @return {Boolean} True if we can see the Rates Evolution tab
   */
  @computed('args.{selectedOTA.hasRatesEvolution,ratesEvolutionDisabled}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showRatesEvolutionTab() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      this.args.selectedOTA?.hasRatesEvolution &&
      !this.args.ratesEvolutionDisabled
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get today() {
    return this.hotelAndMarketService.getLocalTime();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleTabIds() {
    if (this.args.isPricingAssistantProduct) {
      return this.visibleTabIdsForPricingAssistantProduct;
    }

    const tabs = [];

    if (this.showRatesEvolutionTab) {
      tabs.push(DETAIL_TABS.RATE_EVOLUTION);
    }

    if (this.hasBookingCurve) {
      tabs.push(DETAIL_TABS.BOOKING_CURVE);
    }

    return tabs;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleTabIdsForPricingAssistantProduct() {
    const tabs = [DETAIL_TABS.PRICING];

    // "Other" and "Unmapped" room types are not supported in Rates evolution tab
    const isValidRIRoomTypeSelected = ![
      OPEN_ROOM_PRICING_ROOM_CATEGORIES.OTHER,
      OPEN_ROOM_PRICING_ROOM_CATEGORIES.UNMAPPED,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    ].includes(this.args.ratesParams.room);

    if (isValidRIRoomTypeSelected) {
      if (this.showRatesEvolutionTab) {
        tabs.push(DETAIL_TABS.RATE_EVOLUTION);
      }
    }

    if (this.hasBookingCurve) {
      tabs.push(DETAIL_TABS.BOOKING_CURVE);
    }

    return tabs;
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    this.resize.registerResizeEvent(
      RESIZE_EVENTS.MODAL,
      this.resetChartHeight.bind(this),
    );
  }

  willDestroy(): void {
    super.willDestroy();

    this.resize.unregisterResizeEvent(RESIZE_EVENTS.MODAL);
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getValueOfRate(rate) {
    if (rate === null || rate === undefined) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (this.rates?.mealPlan > 0) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return this.intl.t(`rates.meal.missing.${this.rates?.mealPlan}`);
      }
      return this.intl.t('general.missing');
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (rate.value === 0) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      return this.intl.t(rate.message);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return rate.value;
  }

  /**
   * Changes date parameter through the picker in the modal header
   * @param  {Boolean} paramNext True if you clicked the next button
   */
  @action
  // @ts-expect-error: Incorrect type
  dayChange(paramNext): void {
    // Extra protection against changing dates to illegal states
    if ((paramNext && !this.endReached) || (!paramNext && !this.beginReached)) {
      // @ts-expect-error: Incorrect type
      this.args.changeDay(paramNext);
    }
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleExport() {
    // @ts-expect-error: Incorrect type
    if (this.args.currentHotel?.loading || this.args.model?.loading) {
      return false;
    }

    // @ts-expect-error: Incorrect type
    let hotels;
    let headers;
    let title;
    let sheetName;
    let content;

    if (this.args.type === DETAIL_TABS.BOOKING_CURVE) {
      headers = [
        this.intl.t('rates.booking_curve.leadtime').toString(),
        this.intl.t('rates.total_OTB').toString(),
      ];

      if (this.access.hasFeature('pms.transient_group_split')) {
        headers.push(this.intl.t('rates.booking_curve.groups').toString());
        headers.push(this.intl.t('rates.booking_curve.individual').toString());
      }

      // @ts-expect-error: Incorrect type
      const outOfOrderRooms = new OutOfOrderRooms(getOwner(this));

      headers.push(outOfOrderRooms.inventoryText);

      if (this.access.hasFeature('pms.transient_group_split')) {
        headers.push(this.intl.t('general.last_year').toString());
      }

      if (
        this.access.hasShortTermRentalAccess &&
        !this.access.hasShortTermRentalOnlyStrTab
      ) {
        headers.push(this.intl.t('short_term_rental.rate'));
      }

      title = this.day;

      sheetName = this.intl.t('rates.booking_curve').toString();

      // Associate last year periods with current periods
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const allPeriods = this.args.model?.periods.map((period) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const resultingPeriod = period;

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.args.model?.periodsLastYear.some((lastYearPeriod) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (period.leadtime === lastYearPeriod.leadtime) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            set(resultingPeriod, 'lastYear', lastYearPeriod);
            return true;
          }
          return false;
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return resultingPeriod;
      });

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      content = allPeriods.reverse().map((period) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const row = [period.leadtime];

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (period.occupancy?.roomsBooked !== OTB_DEFAULT_VALUE) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          row.push(period.occupancy?.roomsBooked);
        } else {
          row.push('');
        }

        if (this.access.hasFeature('pms.transient_group_split')) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (period.occupancy?.roomsBookedGroup !== OTB_DEFAULT_VALUE) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            row.push(period.occupancy?.roomsBookedGroup);
          } else {
            row.push('');
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (period.occupancy?.roomsBookedIndividual !== OTB_DEFAULT_VALUE) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            row.push(period.occupancy?.roomsBookedIndividual);
          } else {
            row.push('');
          }
        }

        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          period.occupancy?.inventoryExcludingOOORooms !== OTB_DEFAULT_VALUE
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          row.push(period.occupancy?.inventoryExcludingOOORooms);
        } else {
          row.push('');
        }

        if (this.access.hasFeature('pms.transient_group_split')) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (period.lastYear?.occupancy?.roomsBooked !== OTB_DEFAULT_VALUE) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            row.push(period.lastYear?.occupancy?.roomsBooked);
          } else {
            row.push('');
          }
        }

        if (
          this.access.hasShortTermRentalAccess &&
          !this.access.hasShortTermRentalOnlyStrTab
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          row.push(period.day?.rentalOccupancies?.[0]?.value);
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return row;
      });
    } else {
      // @ts-expect-error: Incorrect type
      hotels = [this.args.currentHotel?.hotelinfo];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment
      hotels = [...hotels, ...this.competitorsForCurrentHotelAndOTA];

      headers = [this.intl.t('rates.booking_curve.leadtime').toString()];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      headers = [...headers, ...hotels.map((hotel) => hotel.name)];

      if (
        this.access.hasShortTermRentalAccess &&
        !this.access.hasShortTermRentalOnlyStrTab
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        headers = [...headers, this.intl.t('short_term_rental.rate')];
      }

      title = this.day;

      sheetName = this.intl.t('rates.evolution').toString();

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      content = this.args.model?.periods
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort((a, b) => a.id - b.id)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .reverse()
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .map((period) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          let row = [period.leadtime];

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const data = hotels.map((hotel) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const rate = get(period, `rates.${hotel.id}`);

            if (rate !== undefined) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              const selectedRate = rate[0];

              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (selectedRate && selectedRate.value !== undefined) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const value = this.getValueOfRate(selectedRate);

                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
                return value.string ? value.string : value;
              }
            }

            return 'missing';
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment
          row = [...row, ...data];

          if (
            this.access.hasShortTermRentalAccess &&
            !this.access.hasShortTermRentalOnlyStrTab
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            row.push(period.day?.rentalRates?.[0]?.value);
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return row;
        });
    }

    const postData = JSON.stringify({
      headers,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      content,
      title,
      sheetName,
    });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.simpleExport.execute(
      postData,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.currentHotel.id,
      true,
      // @ts-expect-error: Incorrect type
      'rates-evolution-export',
    );

    this.usageAnalytics.track('Data Exported', {
      eventTrigger: 'detail-modal',
      exportType: this.args.type,
      days: 1,
    });

    return true;
  }

  @action
  // @ts-expect-error: Incorrect type
  makeActiveTab(tabId): void {
    // @ts-expect-error: Incorrect type
    this.args.updateType(tabId);
  }

  @action
  redirectOnClose(): void {
    // @ts-expect-error: Incorrect type
    this.args.redirect();
  }

  @action
  resetChartHeight(): void {
    scheduleOnce('afterRender', this, () => {
      const modal = document.querySelector('.modal--stretched');

      // @ts-expect-error: Incorrect type
      set(this, 'width', modal?.offsetWidth);
      // @ts-expect-error: Incorrect type
      set(this, 'height', modal?.offsetHeight - HEADER_HEIGHT);
    });
  }

  <template>
    {{! template-lint-disable no-duplicate-id }}
    <PrismModal
      @onClose={{this.redirectOnClose}}
      @title={{this.day}}
      class={{if @isPricingAssistantProduct "pricing-assistant-day-detail"}}
      {{didInsert this.resetChartHeight}}
    >
      <:navigation>
        <GeneralModalsModalHeaderDateSelection
          @beginReached={{this.beginReached}}
          @changeDayAction={{this.dayChange}}
          @endReached={{this.endReached}}
        />
      </:navigation>

      <:actions>
        <div class="flex items-center">
          {{! We don't show export button for pricing tab }}
          {{#unless (eq @type DETAIL_TABS.PRICING)}}
            <GeneralButtonsButtonExport @exportAction={{this.handleExport}} />
          {{/unless}}
        </div>
      </:actions>

      <:default>
        <div
          class="day-detail modal--stretched"
          {{didInsert this.resetChartHeight}}
        >
          {{! @glint-expect-error: Incorrect type }}
          {{#if @model.loading}}
            <PrismLoadingBar />
          {{/if}}

          {{#if this.access.isPricingAssistantStandaloneVersion}}
            {{! Note: In case of an update, don't forget to update the PA in RI instance of the pricing tab }}
            <HotelPricingAssistantDayDetailTabsTabPricing
              {{! @glint-expect-error: Incorrect type }}
              @currentHotel={{@currentHotel}}
              {{! @glint-expect-error: Incorrect type }}
              @date={{@date}}
              {{! @glint-expect-error: Incorrect type }}
              @dayModel={{this.dayModel}}
              {{! @glint-expect-error: Incorrect type }}
              @filteredRatesParamsForPricingAssistant={{this.filteredRatesParamsForPricingAssistant}}
              {{! @glint-expect-error: Incorrect type }}
              @isEditActive={{@edit}}
              {{! @glint-expect-error: Incorrect type }}
              @isLoading={{@model.loading}}
              {{! @glint-expect-error: Incorrect type }}
              @model={{@model}}
              {{! @glint-expect-error: Incorrect type }}
              @ratesParams={{@ratesParams}}
              {{! @glint-expect-error: Incorrect type }}
              @toggleEdit={{@onToggleEdit}}
            />
          {{else}}
            <PrismTabsContainer
              {{! @glint-expect-error: Incorrect type }}
              @activeTabId={{@type}}
              @onSelectTab={{this.makeActiveTab}}
              @visibleTabIds={{this.visibleTabIds}}
              as |TabsContainer|
            >
              <TabsContainer.header>
                <:default as |Header|>
                  <Header.tab
                    @id={{DETAIL_TABS.PRICING}}
                    data-test-id="day-detail-pricing-tab-header"
                  >
                    {{t "pricing_assistant.tab.label"}}
                  </Header.tab>
                  <Header.tab
                    @id={{DETAIL_TABS.RATE_EVOLUTION}}
                    data-test-id="day-detail-rate-evolution-tab-header"
                  >
                    {{t "rates.evolution"}}
                  </Header.tab>
                  <Header.tab
                    @id={{DETAIL_TABS.BOOKING_CURVE}}
                    data-test-id="day-detail-booking-curve-tab-header"
                  >
                    {{t "general.pace"}}
                  </Header.tab>
                </:default>
                <:after>
                  {{! We don't show events icon for pricing tab }}
                  {{#unless (eq @type DETAIL_TABS.PRICING)}}
                    {{#if this.access.hasEvents}}
                      <GeneralDayDetailDayDetailEvents @day={{this.dayModel}} />
                    {{/if}}
                  {{/unless}}

                  {{#if this.hasOtbSelfServiceButton}}
                    <GeneralButtonsOtbSelfServiceButton
                      @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.RATES_EVOLUTION_CHART}}
                      @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.RATE_INSIGHT}}
                    />
                  {{/if}}
                </:after>
              </TabsContainer.header>
              <TabsContainer.content as |Content|>
                <Content.tab @id={{DETAIL_TABS.PRICING}}>
                  {{#if this.access.hasPricingAssistantOpenPricingFeature}}
                    <HotelPricingAssistantDayDetailTabsTabOpenPricing
                      {{! @glint-expect-error: Incorrect type }}
                      @date={{@date}}
                      {{! @glint-expect-error: Incorrect type }}
                      @dayModel={{this.dayModel}}
                      {{! @glint-expect-error: Incorrect type }}
                      @isLoading={{@model.loading}}
                      {{! @glint-expect-error: Incorrect type }}
                      @model={{@model}}
                      {{! @glint-expect-error: Incorrect type }}
                      @ratesParams={{@ratesParams}}
                    />
                  {{else}}
                    {{! Note: In case of an update, don't forget to update the standalone instance of the pricing tab }}
                    <HotelPricingAssistantDayDetailTabsTabPricing
                      {{! @glint-expect-error: Incorrect type }}
                      @currentHotel={{@currentHotel}}
                      {{! @glint-expect-error: Incorrect type }}
                      @date={{@date}}
                      {{! @glint-expect-error: Incorrect type }}
                      @dayModel={{this.dayModel}}
                      {{! @glint-expect-error: Incorrect type }}
                      @filteredRatesParamsForPricingAssistant={{this.filteredRatesParamsForPricingAssistant}}
                      {{! @glint-expect-error: Incorrect type }}
                      @isEditActive={{@edit}}
                      {{! @glint-expect-error: Incorrect type }}
                      @isLoading={{@model.loading}}
                      {{! @glint-expect-error: Incorrect type }}
                      @model={{@model}}
                      {{! @glint-expect-error: Incorrect type }}
                      @ratesParams={{@ratesParams}}
                      {{! @glint-expect-error: Incorrect type }}
                      @toggleEdit={{@onToggleEdit}}
                    />
                  {{/if}}
                </Content.tab>
                <Content.tab @id={{DETAIL_TABS.RATE_EVOLUTION}}>
                  {{! @glint-expect-error: Incorrect type }}
                  <GeneralDayDetailTabsTabRatesEvolution
                    @filteredRatesParamsForPricingAssistant={{this.filteredRatesParamsForPricingAssistant}}
                    {{! @glint-expect-error: Incorrect type }}
                    @isLoading={{@model.loading}}
                    @isPricingAssistantProduct={{@isPricingAssistantProduct}}
                    @params={{hash
                      activeCompset=@activeCompset
                      competitorsForCurrentHotelAndOTA=this.competitorsForCurrentHotelAndOTA
                      currency=@currency
                      currentHotel=@currentHotel
                      dayMoment=this.dayMoment
                      hasOtbSelfServiceEntryPoint=@hasOtbSelfServiceEntryPoint
                      height=this.height
                      isBestFlex=@isBestFlex
                      model=@model
                      offset=@offset
                      otaId=@otaId
                      paceTabFilterMessage=@paceTabFilterMessage
                      ratesParams=@ratesParams
                      updateOffset=@updateOffset
                      width=this.width
                    }}
                  />
                </Content.tab>
                <Content.tab @id={{DETAIL_TABS.BOOKING_CURVE}}>
                  {{! @glint-expect-error: Incorrect type }}
                  <GeneralDayDetailTabsTabBookingCurve
                    @dayMoment={{this.dayMoment}}
                    {{! @glint-expect-error: Incorrect type }}
                    @isLoading={{@model.loading}}
                    @params={{hash
                      activeCompset=@activeCompset
                      competitorsForCurrentHotelAndOTA=this.competitorsForCurrentHotelAndOTA
                      currentHotel=@currentHotel
                      height=this.height
                      isBestFlex=@isBestFlex
                      model=@model
                      offset=@offset
                      otaId=@otaId
                      paceTabFilterMessage=@paceTabFilterMessage
                      ratesParams=@ratesParams
                      updateOffset=@updateOffset
                      width=this.width
                    }}
                  />
                </Content.tab>
              </TabsContainer.content>
            </PrismTabsContainer>
          {{/if}}
        </div>
      </:default>
    </PrismModal>
  </template>
}
